'use client';

import { c as _c } from "react/compiler-runtime";
import type { HofDashboardDocument, HofDashboardEntry, HofDashboardForm } from '@/features/hof-dashboard/api/hof-dashboard-data';
import { AreaIcon, LABEL_CLASS, PRIMARY_BUTTON_CLASS, ProgressLine, SECONDARY_BUTTON_CLASS, SectionHeading, StatusPill } from '@/features/hof-dashboard/components/dashboard-ui';
import { DocumentLinks } from '@/features/hof-dashboard/components/document-links';
import { EntryAnswers } from '@/features/hof-dashboard/components/entry-answers';
import { ReviewPanel } from '@/features/hof-dashboard/components/review-panel';
import { EntryTimeline } from '@/features/hof-dashboard/components/review-timeline';
import { HOF_DASHBOARD_AREA_LABELS, HOF_ENTRY_STATUS_LABELS, type HofEntryStatus } from '@/features/hof-dashboard/constants';
import { useWarnBeforeLeaving } from '@/features/hof-dashboard/hooks/use-warn-before-leaving';
import { useWithdrawSubmission } from '@/features/hof-dashboard/hooks/use-withdraw-submission';
import { formatDate, translate, type TextKey } from '@/features/hof-dashboard/texts';
import { entryHeading } from '@/features/hof-dashboard/utils/entry-heading';
import type { SubmissionProgress } from '@/features/hof-dashboard/utils/submission-progress';
import AccordionItem from '@/features/payload-cms/components/accordion/accordion-item';
import { FormBlock } from '@/features/payload-cms/components/form';
import { trpc } from '@/trpc/client';
import type { Locale } from '@/types/types';
import { cn } from '@/utils/tailwindcss-override';
import { ShoppingCart, Trash2, Upload } from 'lucide-react';
import type React from 'react';
import { useCallback, useId, useMemo, useState } from 'react';
import { toast } from 'sonner';
const ENTRY_TONE: Record<HofEntryStatus, 'done' | 'neutral' | 'attention'> = {
  submitted: 'neutral',
  inReview: 'neutral',
  revisionRequired: 'attention',
  accepted: 'done'
};

/** Takes a submission back after a second, inline question: no dialog over the page. */
const WithdrawAction: React.FC<{
  locale: Locale;
  onWithdraw: () => Promise<void>;
}> = t0 => {
  const $ = _c(31);
  const {
    locale,
    onWithdraw
  } = t0;
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const focusOnMount = _temp;
  if (!asking) {
    let t1;
    let t2;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = () => setAsking(true);
      t2 = <Trash2 className="h-4 w-4" aria-hidden={true} />;
      $[0] = t1;
      $[1] = t2;
    } else {
      t1 = $[0];
      t2 = $[1];
    }
    let t3;
    if ($[2] !== locale) {
      t3 = translate("withdraw", locale);
      $[2] = locale;
      $[3] = t3;
    } else {
      t3 = $[3];
    }
    let t4;
    if ($[4] !== t3) {
      t4 = <button type="button" className="inline-flex min-h-10 cursor-pointer items-center gap-1.5 rounded-lg px-2 text-sm font-semibold text-gray-500 transition hover:bg-gray-100 hover:text-gray-700" onClick={t1}>{t2}{t3}</button>;
      $[4] = t3;
      $[5] = t4;
    } else {
      t4 = $[5];
    }
    return t4;
  }
  let t1;
  if ($[6] !== locale) {
    t1 = translate("withdrawQuestion", locale);
    $[6] = locale;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  let t2;
  if ($[8] !== t1) {
    t2 = <p className="text-sm font-medium text-red-900">{t1}</p>;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] !== busy || $[11] !== onWithdraw) {
    t3 = () => {
      if (busy) {
        return;
      }
      setBusy(true);
      onWithdraw().finally(() => {
        setBusy(false);
        setAsking(false);
      });
    };
    $[10] = busy;
    $[11] = onWithdraw;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Trash2 className="h-4 w-4" aria-hidden={true} />;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== locale) {
    t5 = translate("withdrawConfirm", locale);
    $[14] = locale;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== busy || $[17] !== t3 || $[18] !== t5) {
    t6 = <button type="button" aria-disabled={busy} className="bg-cevi-red inline-flex min-h-10 cursor-pointer items-center gap-2 rounded-lg px-4 text-sm font-bold text-white transition hover:opacity-90 aria-disabled:opacity-50" onClick={t3}>{t4}{t5}</button>;
    $[16] = busy;
    $[17] = t3;
    $[18] = t5;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => setAsking(false);
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== locale) {
    t8 = translate("cancel", locale);
    $[21] = locale;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== t8) {
    t9 = <button type="button" className={SECONDARY_BUTTON_CLASS} onClick={t7} ref={focusOnMount}>{t8}</button>;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== t6 || $[26] !== t9) {
    t10 = <div className="flex gap-2">{t6}{t9}</div>;
    $[25] = t6;
    $[26] = t9;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== t10 || $[29] !== t2) {
    t11 = <div role="group" className="flex flex-wrap items-center gap-3 rounded-lg border border-red-200 bg-red-50 p-3">{t2}{t10}</div>;
    $[28] = t10;
    $[29] = t2;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  return t11;
};

/**
 * One submission: where it stands, its history of who handed it in and every answer and
 * comment of the Ressort, and what the Hof handed in.
 */
const EntryBlock: React.FC<{
  entry: HofDashboardEntry;
  heading: string;
  /** Off for the version that counts: the card's own status already says where it stands. */
  showStatus?: boolean;
  /** Set for a reviewer, who answers the submission here instead. */
  reviewFor?: string | undefined;
  /** Whether a reviewer can mark it final: the version that counts of a form of versions. */
  canFinalize?: boolean;
  locale: Locale;
  onWithdraw: (id: string) => Promise<void>;
}> = t0 => {
  const $ = _c(32);
  const {
    entry,
    heading,
    showStatus: t1,
    reviewFor,
    canFinalize: t2,
    locale,
    onWithdraw
  } = t0;
  const showStatus = t1 === undefined ? true : t1;
  const canFinalize = t2 === undefined ? false : t2;
  let t3;
  if ($[0] !== heading) {
    t3 = <h4 className="text-sm font-bold text-gray-900">{heading}</h4>;
    $[0] = heading;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== entry.status || $[3] !== locale || $[4] !== showStatus) {
    t4 = showStatus && <StatusPill tone={ENTRY_TONE[entry.status]}>{HOF_ENTRY_STATUS_LABELS[entry.status][locale]}</StatusPill>;
    $[2] = entry.status;
    $[3] = locale;
    $[4] = showStatus;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t3 || $[7] !== t4) {
    t5 = <div className="flex flex-wrap items-center gap-x-3 gap-y-1">{t3}{t4}</div>;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== entry || $[10] !== locale) {
    t6 = <EntryTimeline entry={entry} locale={locale} />;
    $[9] = entry;
    $[10] = locale;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== entry.answers || $[13] !== locale) {
    t7 = <EntryAnswers answers={entry.answers} locale={locale} />;
    $[12] = entry.answers;
    $[13] = locale;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== canFinalize || $[16] !== entry || $[17] !== locale || $[18] !== reviewFor) {
    t8 = reviewFor !== undefined && <ReviewPanel entry={entry} hofId={reviewFor} canFinalize={canFinalize} locale={locale} />;
    $[15] = canFinalize;
    $[16] = entry;
    $[17] = locale;
    $[18] = reviewFor;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== entry.id || $[21] !== entry.withdrawable || $[22] !== locale || $[23] !== onWithdraw || $[24] !== reviewFor) {
    t9 = entry.withdrawable && reviewFor === undefined && <WithdrawAction locale={locale} onWithdraw={() => onWithdraw(entry.id)} />;
    $[20] = entry.id;
    $[21] = entry.withdrawable;
    $[22] = locale;
    $[23] = onWithdraw;
    $[24] = reviewFor;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== t5 || $[27] !== t6 || $[28] !== t7 || $[29] !== t8 || $[30] !== t9) {
    t10 = <div className="space-y-4">{t5}{t6}{t7}{t8}{t9}</div>;
    $[26] = t5;
    $[27] = t6;
    $[28] = t7;
    $[29] = t8;
    $[30] = t9;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  return t10;
};

/** The label of the button that opens the form, after what there is already. */
const actionLabel = (form: HofDashboardForm): TextKey => {
  const first = form.entries.length === 0;
  if (form.area === 'material') return first ? 'placeOrder' : 'changeOrder';
  if (first) return 'handIn';
  return form.mode === 'versions' ? 'handInNewVersion' : 'handInAnother';
};

/** What the button's neighbour says about handing in again; nothing for a form of entries. */
const actionHint = (form: HofDashboardForm): TextKey | undefined => {
  if (form.mode === 'entries') return undefined;
  return form.area === 'material' ? 'orderHint' : 'versionsHint';
};

/** What the Hof handed in so far: the newest version with the earlier ones folded away below. */
const Submissions: React.FC<{
  form: HofDashboardForm;
  hofId: string;
  isReviewer: boolean;
  locale: Locale;
}> = t0 => {
  const $ = _c(42);
  const {
    form,
    hofId,
    isReviewer,
    locale
  } = t0;
  const [showEarlier, setShowEarlier] = useState(false);
  const withdraw = useWithdrawSubmission(hofId, locale);
  const earlierId = useId();
  let current;
  let earlier;
  if ($[0] !== form.entries) {
    [current, ...earlier] = form.entries;
    $[0] = form.entries;
    $[1] = current;
    $[2] = earlier;
  } else {
    current = $[1];
    earlier = $[2];
  }
  if (current === undefined) {
    let t1;
    if ($[3] !== locale) {
      t1 = translate("nothingYet", locale);
      $[3] = locale;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    let t2;
    if ($[5] !== t1) {
      t2 = <p className="text-base font-semibold text-green-900">{t1}</p>;
      $[5] = t1;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    let t3;
    if ($[7] !== isReviewer || $[8] !== locale) {
      t3 = !isReviewer && <p className="mt-1 text-sm text-green-700">{translate("nothingYetHint", locale)}</p>;
      $[7] = isReviewer;
      $[8] = locale;
      $[9] = t3;
    } else {
      t3 = $[9];
    }
    let t4;
    if ($[10] !== t2 || $[11] !== t3) {
      t4 = <div className="border-line rounded-xl border border-dashed p-5">{t2}{t3}</div>;
      $[10] = t2;
      $[11] = t3;
      $[12] = t4;
    } else {
      t4 = $[12];
    }
    return t4;
  }
  if (form.mode === "entries") {
    let t1;
    if ($[13] !== form || $[14] !== hofId || $[15] !== isReviewer || $[16] !== locale || $[17] !== withdraw) {
      t1 = form.entries.map((entry, index) => <div key={entry.id} className="py-4"><EntryBlock entry={entry} heading={entryHeading(form, index, locale)} locale={locale} reviewFor={isReviewer ? hofId : undefined} onWithdraw={withdraw} /></div>);
      $[13] = form;
      $[14] = hofId;
      $[15] = isReviewer;
      $[16] = locale;
      $[17] = withdraw;
      $[18] = t1;
    } else {
      t1 = $[18];
    }
    let t2;
    if ($[19] !== t1) {
      t2 = <div className="divide-line border-line divide-y border-y">{t1}</div>;
      $[19] = t1;
      $[20] = t2;
    } else {
      t2 = $[20];
    }
    return t2;
  }
  let t1;
  if ($[21] !== form || $[22] !== locale) {
    t1 = entryHeading(form, 0, locale);
    $[21] = form;
    $[22] = locale;
    $[23] = t1;
  } else {
    t1 = $[23];
  }
  const t2 = isReviewer ? hofId : undefined;
  let t3;
  if ($[24] !== current || $[25] !== locale || $[26] !== t1 || $[27] !== t2 || $[28] !== withdraw) {
    t3 = <EntryBlock entry={current} heading={t1} showStatus={false} reviewFor={t2} canFinalize={true} locale={locale} onWithdraw={withdraw} />;
    $[24] = current;
    $[25] = locale;
    $[26] = t1;
    $[27] = t2;
    $[28] = withdraw;
    $[29] = t3;
  } else {
    t3 = $[29];
  }
  let t4;
  if ($[30] !== earlier || $[31] !== earlierId || $[32] !== form || $[33] !== hofId || $[34] !== isReviewer || $[35] !== locale || $[36] !== showEarlier || $[37] !== withdraw) {
    t4 = earlier.length > 0 && <AccordionItem titleElement={<span className="text-sm font-semibold text-gray-700">{translate("earlierVersions", locale, {
        n: earlier.length
      })}</span>} showChevron={true} accordionId={earlierId} isExpanded={showEarlier} onToggle={() => setShowEarlier(_temp2)} isNested={true}>{showEarlier && <div className="divide-line divide-y">{earlier.map((entry_0, index_0) => <div key={entry_0.id} className="py-4 first:pt-0 last:pb-0"><EntryBlock entry={entry_0} heading={entryHeading(form, index_0 + 1, locale)} locale={locale} reviewFor={isReviewer ? hofId : undefined} onWithdraw={withdraw} /></div>)}</div>}</AccordionItem>;
    $[30] = earlier;
    $[31] = earlierId;
    $[32] = form;
    $[33] = hofId;
    $[34] = isReviewer;
    $[35] = locale;
    $[36] = showEarlier;
    $[37] = withdraw;
    $[38] = t4;
  } else {
    t4 = $[38];
  }
  let t5;
  if ($[39] !== t3 || $[40] !== t4) {
    t5 = <div className="space-y-4">{t3}{t4}</div>;
    $[39] = t3;
    $[40] = t4;
    $[41] = t5;
  } else {
    t5 = $[41];
  }
  return t5;
};

/**
 * One form the Hof hands in, as the detail beside an area's list: where it stands, what to
 * hand in, the button that opens the form in place with the Hof filled in, the area's
 * documents, and what the Hof handed in with the Ressort's feedback. An opened form takes the
 * full width. Kept mounted while another form is shown, so what was typed survives a look at
 * it; `hidden` takes it out of view.
 */
export const FormDetail: React.FC<{
  form: HofDashboardForm;
  progress: SubmissionProgress;
  hofId: string;
  /** A reviewer answers what the Hof handed in, and hands in nothing for it. */
  isReviewer: boolean;
  /** The documents of the form's area. */
  documents: HofDashboardDocument[];
  hidden: boolean;
  locale: Locale;
}> = t0 => {
  const $ = _c(94);
  const {
    form,
    progress,
    hofId,
    isReviewer,
    documents,
    hidden,
    locale
  } = t0;
  const [open, setOpen] = useState(false);
  const utils = trpc.useUtils();
  const titleId = useId();
  useWarnBeforeLeaving(open);
  let t1;
  let t2;
  if ($[0] !== form.hofField || $[1] !== hofId) {
    t2 = {
      [form.hofField]: hofId
    };
    $[0] = form.hofField;
    $[1] = hofId;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  t1 = t2;
  const presetValues = t1;
  const revisionAsked = progress.gap === "revision";
  const mayHandIn = !isReviewer && !form.closed && !form.finalized;
  let t10;
  let t11;
  let t12;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[3] !== form || $[4] !== hidden || $[5] !== isReviewer || $[6] !== locale || $[7] !== mayHandIn || $[8] !== open || $[9] !== progress || $[10] !== revisionAsked || $[11] !== titleId) {
    const hint = actionHint(form);
    t3 = form.id;
    t4 = -1;
    t5 = hidden;
    t6 = titleId;
    t7 = "border-line @container scroll-mt-24 space-y-6 rounded-[14px] border bg-white p-5 outline-none focus-visible:ring-2 focus-visible:ring-green-600 @xl:p-8";
    let t13;
    if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
      t13 = cn(LABEL_CLASS, "flex items-center gap-2");
      $[22] = t13;
    } else {
      t13 = $[22];
    }
    let t14;
    if ($[23] !== form.area) {
      t14 = <AreaIcon area={form.area} />;
      $[23] = form.area;
      $[24] = t14;
    } else {
      t14 = $[24];
    }
    const t15 = HOF_DASHBOARD_AREA_LABELS[form.area][locale];
    let t16;
    if ($[25] !== t14 || $[26] !== t15) {
      t16 = <p className={t13}>{t14}{t15}</p>;
      $[25] = t14;
      $[26] = t15;
      $[27] = t16;
    } else {
      t16 = $[27];
    }
    let t17;
    if ($[28] !== form.title || $[29] !== titleId) {
      t17 = <h3 id={titleId} className="font-heading text-heading text-2xl font-extrabold text-balance @xl:text-3xl">{form.title}</h3>;
      $[28] = form.title;
      $[29] = titleId;
      $[30] = t17;
    } else {
      t17 = $[30];
    }
    let t18;
    if ($[31] !== locale || $[32] !== progress) {
      t18 = <ProgressLine progress={progress} locale={locale} />;
      $[31] = locale;
      $[32] = progress;
      $[33] = t18;
    } else {
      t18 = $[33];
    }
    if ($[34] !== t16 || $[35] !== t17 || $[36] !== t18) {
      t8 = <header className="space-y-2">{t16}{t17}{t18}</header>;
      $[34] = t16;
      $[35] = t17;
      $[36] = t18;
      $[37] = t8;
    } else {
      t8 = $[37];
    }
    if ($[38] !== form.description || $[39] !== isReviewer || $[40] !== locale) {
      t9 = form.description !== undefined && form.description !== "" && <div className="bg-quiet rounded-xl px-5 py-4"><p className="text-base font-semibold text-green-900">{translate(isReviewer ? "whatTheHofHandsIn" : "whatYouHandIn", locale)}</p><p className="mt-1 text-base text-pretty whitespace-pre-line text-green-800">{form.description}</p></div>;
      $[38] = form.description;
      $[39] = isReviewer;
      $[40] = locale;
      $[41] = t9;
    } else {
      t9 = $[41];
    }
    if ($[42] !== form.closed || $[43] !== form.deadline || $[44] !== locale) {
      t10 = form.closed && form.deadline !== undefined && <p className="bg-quiet rounded-xl px-5 py-4 text-sm text-green-800">{translate("formClosed", locale, {
          date: formatDate(form.deadline, locale)
        })}</p>;
      $[42] = form.closed;
      $[43] = form.deadline;
      $[44] = locale;
      $[45] = t10;
    } else {
      t10 = $[45];
    }
    if ($[46] !== form.finalized || $[47] !== isReviewer || $[48] !== locale) {
      t11 = form.finalized && !isReviewer && <p className="bg-quiet rounded-xl px-5 py-4 text-sm text-green-800">{translate("finalized", locale)}</p>;
      $[46] = form.finalized;
      $[47] = isReviewer;
      $[48] = locale;
      $[49] = t11;
    } else {
      t11 = $[49];
    }
    t12 = mayHandIn && !open && <div className="flex flex-wrap items-center gap-x-5 gap-y-2"><button type="button" className={revisionAsked || form.entries.length === 0 ? PRIMARY_BUTTON_CLASS : SECONDARY_BUTTON_CLASS} onClick={() => setOpen(true)}>{form.area === "material" ? <ShoppingCart aria-hidden={true} /> : <Upload aria-hidden={true} />}{translate(actionLabel(form), locale)}</button>{hint !== undefined && <p className="max-w-md text-sm text-pretty text-green-700">{translate(hint, locale)}</p>}</div>;
    $[3] = form;
    $[4] = hidden;
    $[5] = isReviewer;
    $[6] = locale;
    $[7] = mayHandIn;
    $[8] = open;
    $[9] = progress;
    $[10] = revisionAsked;
    $[11] = titleId;
    $[12] = t10;
    $[13] = t11;
    $[14] = t12;
    $[15] = t3;
    $[16] = t4;
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
    $[21] = t9;
  } else {
    t10 = $[12];
    t11 = $[13];
    t12 = $[14];
    t3 = $[15];
    t4 = $[16];
    t5 = $[17];
    t6 = $[18];
    t7 = $[19];
    t8 = $[20];
    t9 = $[21];
  }
  let t13;
  if ($[50] !== form.form || $[51] !== form.initialValues || $[52] !== hofId || $[53] !== locale || $[54] !== mayHandIn || $[55] !== open || $[56] !== presetValues || $[57] !== utils) {
    t13 = mayHandIn && open && <div className="bg-page border-line space-y-2 rounded-xl border p-4 @xl:p-6"><FormBlock form={form.form} withBorder={false} presetValues={presetValues} initialValues={form.initialValues} onSubmitted={() => {
        setOpen(false);
        toast.success(translate("handedIn", locale));
        utils.hofDashboard.getHofDashboard.invalidate({
          hofId
        });
      }} /><button type="button" className={SECONDARY_BUTTON_CLASS} onClick={() => setOpen(false)}>{translate("cancel", locale)}</button></div>;
    $[50] = form.form;
    $[51] = form.initialValues;
    $[52] = hofId;
    $[53] = locale;
    $[54] = mayHandIn;
    $[55] = open;
    $[56] = presetValues;
    $[57] = utils;
    $[58] = t13;
  } else {
    t13 = $[58];
  }
  const t14 = !isReviewer && documents.length > 0 && "@min-[600px]:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]";
  let t15;
  if ($[59] !== t14) {
    t15 = cn("grid gap-8", t14);
    $[59] = t14;
    $[60] = t15;
  } else {
    t15 = $[60];
  }
  const t16 = isReviewer ? "hofSubmissions" : "yourSubmissions";
  let t17;
  if ($[61] !== locale || $[62] !== t16) {
    t17 = translate(t16, locale);
    $[61] = locale;
    $[62] = t16;
    $[63] = t17;
  } else {
    t17 = $[63];
  }
  let t18;
  if ($[64] !== t17) {
    t18 = <SectionHeading>{t17}</SectionHeading>;
    $[64] = t17;
    $[65] = t18;
  } else {
    t18 = $[65];
  }
  let t19;
  if ($[66] !== form || $[67] !== hofId || $[68] !== isReviewer || $[69] !== locale) {
    t19 = <Submissions form={form} hofId={hofId} isReviewer={isReviewer} locale={locale} />;
    $[66] = form;
    $[67] = hofId;
    $[68] = isReviewer;
    $[69] = locale;
    $[70] = t19;
  } else {
    t19 = $[70];
  }
  let t20;
  if ($[71] !== t18 || $[72] !== t19) {
    t20 = <section className="min-w-0 space-y-3">{t18}{t19}</section>;
    $[71] = t18;
    $[72] = t19;
    $[73] = t20;
  } else {
    t20 = $[73];
  }
  let t21;
  if ($[74] !== documents || $[75] !== locale) {
    t21 = documents.length > 0 && <section className="min-w-0 space-y-1 @min-[600px]:order-first"><SectionHeading>{translate("formDocuments", locale)}</SectionHeading><DocumentLinks documents={documents} locale={locale} /></section>;
    $[74] = documents;
    $[75] = locale;
    $[76] = t21;
  } else {
    t21 = $[76];
  }
  let t22;
  if ($[77] !== t15 || $[78] !== t20 || $[79] !== t21) {
    t22 = <div className={t15}>{t20}{t21}</div>;
    $[77] = t15;
    $[78] = t20;
    $[79] = t21;
    $[80] = t22;
  } else {
    t22 = $[80];
  }
  let t23;
  if ($[81] !== t10 || $[82] !== t11 || $[83] !== t12 || $[84] !== t13 || $[85] !== t22 || $[86] !== t3 || $[87] !== t4 || $[88] !== t5 || $[89] !== t6 || $[90] !== t7 || $[91] !== t8 || $[92] !== t9) {
    t23 = <article data-form={t3} tabIndex={t4} hidden={t5} aria-labelledby={t6} className={t7}>{t8}{t9}{t10}{t11}{t12}{t13}{t22}</article>;
    $[81] = t10;
    $[82] = t11;
    $[83] = t12;
    $[84] = t13;
    $[85] = t22;
    $[86] = t3;
    $[87] = t4;
    $[88] = t5;
    $[89] = t6;
    $[90] = t7;
    $[91] = t8;
    $[92] = t9;
    $[93] = t23;
  } else {
    t23 = $[93];
  }
  return t23;
};
function _temp(button) {
  return button?.focus();
}
function _temp2(shown) {
  return !shown;
}